/**
 * ApplicationsPage.tsx — Tenant application management
 *
 * Lists and manages billable applications for the current org.
 * Plans, catalogs, and subscriptions can be scoped to applications.
 *
 * @since 2026-06-19 (SaaS conversion — Phase 9)
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useOrgContext } from '../contexts/OrgContext';
import AdminLayout from '../components/AdminLayout';

interface TenantApp {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
}

export default function ApplicationsPage(): React.JSX.Element {
  const { token, apiBaseUrl } = useAdminAuth();
  const { activeOrg } = useOrgContext();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', description: '' });

  const orgId = activeOrg?.orgId;

  const { data, isLoading } = useQuery({
    queryKey: ['org', orgId, 'apps'],
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/apps`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json() as Promise<{ applications: TenantApp[] }>;
    },
    enabled: !!token && !!orgId,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiBaseUrl}/api/v1/orgs/${orgId}/apps`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      return res.json();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org', orgId, 'apps'] });
      setShowCreate(false);
      setForm({ name: '', slug: '', description: '' });
    },
  });

  const apps = data?.applications ?? [];

  return (
    <AdminLayout title="Applications">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Applications</h2>
            <p className="mt-1 text-sm text-slate-500">
              Manage your billable products. Plans, catalogs, and subscriptions can be scoped to applications.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700"
          >
            + Create Application
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-500">Loading...</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {apps.map((app) => (
              <div
                key={app.id}
                className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{app.name}</h3>
                    <p className="mt-1 text-xs text-slate-500">{app.slug}</p>
                  </div>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      app.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {app.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {app.description && (
                  <p className="mt-3 text-sm text-slate-600">{app.description}</p>
                )}
                <p className="mt-3 text-xs text-slate-400">
                  Created {new Date(app.created_at).toLocaleDateString()}
                </p>
              </div>
            ))}
            {apps.length === 0 && (
              <div className="col-span-full rounded-xl border-2 border-dashed border-slate-200 p-8 text-center text-slate-400">
                No applications yet. Create one to start organizing your billing.
              </div>
            )}
          </div>
        )}

        {/* Create dialog */}
        {showCreate && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900">Create Application</h3>
              <div className="mt-4 space-y-3">
                <input
                  type="text"
                  placeholder="Application Name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <input
                  type="text"
                  placeholder="Slug (e.g. my-saas-app)"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
                <textarea
                  placeholder="Description (optional)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  rows={3}
                />
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowCreate(false)}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createMutation.mutate()}
                  disabled={createMutation.isPending}
                  className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
